import {useEffect, useRef} from "react"
import {Outlet, useLocation, useSearchParams} from "react-router-dom"
import type {Sort} from "shared"

import {DocumentsList} from "@/components/documents/DocumentsList"
import {NewDocumentButton} from "@/components/NewDocumentButton"
import {RestrictedPaymentNotice} from "@/components/RestrictedPaymentNotice"
import {type DocumentTabStatus, isDocumentTabStatus, isSort,} from "@/lib/documents"
import {tabHasMore, useDocumentsStore} from "@/store/documents"

const DEFAULT_TAB: DocumentTabStatus = "ЧЕРНЕТКА"
// documentsListQuery's own default (`SortSchema.default("DESC")`) — newest
// documents first.
const DEFAULT_SORT: Sort = "DESC"

/**
 * documents.md's `/documents` — main page. This app has no
 * `DemoScenariosPanel` equivalent (same gap `SubjectUsers.tsx` documents for
 * its own `restricted` flag), so `restricted` stays a permanently-`false`
 * constant: the branch is correct per spec without a live trigger to reach
 * it.
 *
 * The active tab is router-integrated via a `?tab=` query param (default
 * `ЧЕРНЕТКА`) rather than page-local state, so switching tabs is a real
 * navigation (back/forward, refresh, and shared links all preserve it).
 * Only that one tab's data is fetched from the real `GET /otk/documents`,
 * 30 rows at a time — see `store/documents.ts`.
 *
 * There is no client-side re-filtering of the fetched tab against the URL's
 * other query params here: `DocumentAdvancedSearch` (the real
 * `/documents/search` page) calls `POST /otk/documents/search` directly and
 * renders its own results inline — it never redirects back to `/documents`
 * with search params in the query string the way the wireframe prototype's
 * advanced search did, so there is nothing here for a `matchesAdvancedParams`-
 * style filter to ever match against. `?tab=` (this page's own tab state),
 * `?sort=` (the `Створено` column's backend sort direction — every other
 * column still sorts client-side, see `store/documents.ts`'s `fetchTab`),
 * and `?q=` (the quick-search box's seed) are the only params this route
 * itself reads.
 */
export function Documents() {
  const byStatus = useDocumentsStore((s) => s.byStatus)
  const fetchTab = useDocumentsStore((s) => s.fetchTab)
  const loadMore = useDocumentsStore((s) => s.loadMore)
  const deleteDraft = useDocumentsStore((s) => s.deleteDraft)
  const duplicate = useDocumentsStore((s) => s.duplicate)
  const annul = useDocumentsStore((s) => s.annul)
  const counts = useDocumentsStore((s) => s.counts)
  const refreshCounts = useDocumentsStore((s) => s.refreshCounts)
  const countsRequested = useRef(false)
  const retrySend = useDocumentsStore((s) => s.retrySend)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const restricted = false

  const tabParam = searchParams.get("tab")
  const tab = isDocumentTabStatus(tabParam) ? tabParam : DEFAULT_TAB

  const sortParam = searchParams.get("sort")
  const sort = isSort(sortParam) ? sortParam : DEFAULT_SORT

  // documents.md: "the active row is highlighted" while its document is
  // open in the modal above this list. `Documents` renders the nested
  // `:id/:mode?` route's match as an `Outlet` sibling (not a child
  // component), so `useParams()` here wouldn't see that descendant route's
  // own `id` param — reading it back out of the URL directly instead.
  const activeRowId = location.pathname.match(/^\/documents\/([^/]+)/)?.[1]

  useEffect(() => {
    fetchTab(tab, sort)
  }, [tab, sort, fetchTab])

  // Counts cover every visible tab, including the conditional «Не відправлено» tab.
  useEffect(() => {
    if (countsRequested.current) return
    countsRequested.current = true
    void refreshCounts()
  }, [refreshCounts])

  const hasUnsent = counts === null ? null : counts.SUBMISSION_PENDING !== 0

  // "If its last document leaves while the tab is active, the tab disappears
  // and the list falls back to «Чернетка»." Only once the probe has actually
  // answered `false` — `null` means not yet known, and redirecting on that
  // would bounce anyone opening `?tab=НЕ ВІДПРАВЛЕНО` directly.
  useEffect(() => {
    if (tab !== "НЕ ВІДПРАВЛЕНО" || hasUnsent !== false) return
    const next = new URLSearchParams(searchParams)
    next.set("tab", DEFAULT_TAB)
    setSearchParams(next, { replace: true })
  }, [hasUnsent, searchParams, setSearchParams, tab])

  const tabState = byStatus[tab]

  function handleTabChange(nextTab: DocumentTabStatus) {
    const next = new URLSearchParams(searchParams)
    next.set("tab", nextTab)
    setSearchParams(next)
  }

  function handleSortChange(nextSort: Sort) {
    const next = new URLSearchParams(searchParams)
    next.set("sort", nextSort)
    setSearchParams(next)
  }

  return (
    <>
      {restricted && <RestrictedPaymentNotice />}
      {/* Heading and list share one block (no PageContainer gap between
          them) — the prototype's `mb-3` is the only space above the card. */}
      <div>
        <div className="mb-3 flex justify-between">
          <h1 className="text-2xl font-semibold">Документи ОТК</h1>
          <NewDocumentButton restricted={restricted} sort={sort} />
        </div>
        {tabState.error ? (
          <p className="text-sm text-destructive">{tabState.error}</p>
        ) : (
          <DocumentsList
            activeRowId={activeRowId}
            counts={counts}
            documents={tabState.documents}
            hasMore={tabHasMore(tabState)}
            hasUnsent={hasUnsent === true}
            initialSearch={searchParams.get("q") ?? ""}
            isLoading={tabState.isLoading}
            isLoadingMore={tabState.isLoadingMore}
            onAnnul={annul}
            onDelete={deleteDraft}
            onDuplicate={(id, sectionIds) => duplicate(id, sectionIds, sort)}
            onLoadMore={() => loadMore(tab, sort)}
            onRetrySend={(id) => retrySend(id, sort)}
            onSortChange={handleSortChange}
            onTabChange={handleTabChange}
            sort={sort}
            tab={tab}
          />
        )}
      </div>
      <Outlet />
    </>
  )
}
